import { useState } from 'react';
import { EnrichedClaim } from './types';
import { ClaimStatusBadge } from './ClaimStatusBadge';

interface ClaimsTableProps {
    claims: EnrichedClaim[];
    loading: boolean;
    emptyMessage?: string;
    showGameColumn?: boolean;
    onEdit?: (claim: EnrichedClaim) => void;
}

function TableHeader({ showGameColumn }: { showGameColumn: boolean }) {
    return (
        <thead className="bg-background">
            <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Item</th>
                {showGameColumn && <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Game</th>}
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Status</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Date</th>
                <th className="px-4 lg:px-6 py-3"></th>
            </tr>
        </thead>
    );
}

export function ClaimsTable({
    claims,
    loading,
    emptyMessage = 'No claims found',
    showGameColumn = true,
    onEdit,
}: ClaimsTableProps) {
    const [expandedId, setExpandedId] = useState<string | null>(null);

    if (loading) {
        return (
            <div className="bg-surface rounded-lg shadow overflow-hidden border border-border overflow-x-auto">
                <table className="w-full">
                    <TableHeader showGameColumn={showGameColumn} />
                    <tbody className="bg-surface divide-y divide-border">
                        {[1, 2, 3].map((i) => (
                            <tr key={i}>
                                <td className="px-4 lg:px-6 py-4"><div className="animate-pulse bg-background rounded h-4 w-24 lg:w-32"></div></td>
                                {showGameColumn && <td className="px-4 lg:px-6 py-4"><div className="animate-pulse bg-background rounded h-4 w-20 lg:w-24"></div></td>}
                                <td className="px-4 lg:px-6 py-4"><div className="animate-pulse bg-background rounded h-4 w-16"></div></td>
                                <td className="px-4 lg:px-6 py-4"><div className="animate-pulse bg-background rounded h-4 w-20"></div></td>
                                <td className="px-4 lg:px-6 py-4"><div className="animate-pulse bg-background rounded h-4 w-4"></div></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    if (claims.length === 0) {
        return (
            <div className="bg-surface rounded-lg shadow p-12 text-center border border-border">
                <p className="text-text-secondary">{emptyMessage}</p>
            </div>
        );
    }

    return (
        <div className="bg-surface rounded-lg shadow overflow-hidden border border-border overflow-x-auto">
            <table className="w-full">
                <TableHeader showGameColumn={showGameColumn} />
                <tbody className="bg-surface divide-y divide-border">
                    {claims.map((claim) => {
                        const isExpanded = expandedId === claim.id;
                        return (
                            <ClaimRow
                                key={claim.id}
                                claim={claim}
                                isExpanded={isExpanded}
                                showGameColumn={showGameColumn}
                                onToggle={() => setExpandedId(isExpanded ? null : claim.id)}
                                onEdit={onEdit}
                            />
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

interface ClaimRowProps {
    claim: EnrichedClaim;
    isExpanded: boolean;
    showGameColumn: boolean;
    onToggle: () => void;
    onEdit?: (claim: EnrichedClaim) => void;
}

function ClaimRow({ claim, isExpanded, showGameColumn, onToggle, onEdit }: ClaimRowProps) {
    return (
        <>
            <tr onClick={onToggle} className="hover:bg-background cursor-pointer">
                <td className="px-4 lg:px-6 py-4 text-sm text-text-primary max-w-[200px] truncate" title={claim.itemTitle}>
                    {claim.itemTitle}
                </td>
                {showGameColumn && (
                    <td className="px-4 lg:px-6 py-4 text-sm text-text-secondary max-w-[150px] truncate" title={claim.gameName}>
                        {claim.gameName}
                    </td>
                )}
                <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <ClaimStatusBadge status={claim.status} />
                </td>
                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    {new Date(claim.createdAt).toLocaleDateString()}
                </td>
                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    <span className="inline-flex items-center gap-2">
                        {claim.status === 'pending' && onEdit && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onEdit(claim); }}
                                className="bg-primary text-white rounded px-3 py-1 text-sm hover:opacity-90"
                            >
                                Edit
                            </button>
                        )}
                        <span className="inline-block transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                            ▶
                        </span>
                    </span>
                </td>
            </tr>
            {isExpanded && (
                <tr>
                    <td colSpan={showGameColumn ? 5 : 4} className="px-0 py-0">
                        <div className="bg-background border-l-4 border-primary px-6 py-4 space-y-3">
                            {claim.description && (
                                <div>
                                    <p className="text-xs font-medium text-text-secondary uppercase mb-1">Description</p>
                                    <p className="text-sm text-text-primary">{claim.description}</p>
                                </div>
                            )}

                            {claim.evidenceUrls && claim.evidenceUrls.length > 0 && (
                                <div>
                                    <p className="text-xs font-medium text-text-secondary uppercase mb-1">Evidence</p>
                                    <div className="flex flex-wrap gap-2">
                                        {claim.evidenceUrls.map((url, idx) => (
                                            <a
                                                key={idx}
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary hover:underline text-sm"
                                            >
                                                Link {idx + 1}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {claim.imageUrls && claim.imageUrls.length > 0 && (
                                <div>
                                    <p className="text-xs font-medium text-text-secondary uppercase mb-1">Images</p>
                                    <div className="flex flex-wrap gap-2">
                                        {claim.imageUrls.map((url, idx) => (
                                            <a
                                                key={idx}
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <img
                                                    src={url}
                                                    alt={`Evidence ${idx + 1}`}
                                                    className="w-10 h-10 sm:w-12 sm:h-12 rounded object-cover"
                                                />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {claim.status === 'rejected' && claim.rejectionReason && (
                                <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                                    <p className="text-xs font-medium text-red-400 uppercase mb-1">Rejection Reason:</p>
                                    <p className="text-sm text-text-primary">{claim.rejectionReason}</p>
                                </div>
                            )}

                            {claim.reviewedAt && (
                                <div>
                                    <p className="text-xs font-medium text-text-secondary uppercase mb-1">Reviewed</p>
                                    <p className="text-sm text-text-secondary">
                                        {new Date(claim.reviewedAt).toLocaleDateString()}
                                    </p>
                                </div>
                            )}

                            {claim.status === 'pending' && onEdit && (
                                <div className="pt-2">
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onEdit(claim); }}
                                        className="bg-primary text-white rounded px-3 py-1 text-sm hover:opacity-90"
                                    >
                                        Edit
                                    </button>
                                </div>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
